<script setup lang="ts">
import * as _ from 'lodash-es';
import { onMounted, watch } from 'vue';
import { RouterView } from 'vue-router';
import { storeToRefs } from 'pinia';
import { Icon } from '@iconify/vue';
import { container as WidgetContainerModal } from 'jenesius-vue-modal';
import AppMessage from '@/layout/AppMessage.vue';
import TwoFactorRequiredModal from '@/views/main/user/two-factor-required/TwoFactorRequiredModal.vue';
import { DxLoader, DxLoaderBar, DxLoaderBlock, DxLoaderSpinner, DxToast } from '@repo/vue/components';
import { useDxProgress } from '@repo/vue/utils';
import { useAppStore, useAuthStore } from '@repo/vue/stores';
import { ResourceConfig } from '@repo/core/resources';
import { useDxState } from '@/utils';

// utils
const { isLoadingSpinner } = useDxProgress();
const dxState = useDxState();

// stores
const authStore = useAuthStore();
const appStore = useAppStore();

// state
const { isIFrame, layoutKey } = storeToRefs(appStore);
const { isLoading, user } = storeToRefs(authStore);
// Read at setup so RouterView stays unmounted until logout redirects.
const twoFactorReset = new URLSearchParams(window.location.search).get('twoFactorReset') === '1';

// watches
watch(
  () => user.value?.profile?.email,
  (newValue, oldValue) => {
    if (newValue && newValue !== oldValue) {
      if (
        _.toLower(newValue) !== 'debbie.vanderwalt@gmail.com' &&
        !_.endsWith(_.toLower(newValue), _.toLower('@draftworx.com')) &&
        !isIFrame.value
      ) {
        location.href = ResourceConfig.webAngularUrl;
      }
    }
  },
  { immediate: true }
);

// hooks
onMounted(async () => {
  // A completed 2FA replacement revokes this session's grants; force a re-login. Backing out of the
  // reset arrives without the flag, so the session is left alone.
  if (twoFactorReset) {
    await dxState.logout();
  }
});
</script>

<template>
  <template v-if="isLoading || twoFactorReset">
    <DxLoader />
  </template>
  <template v-else>
    <RouterView :key="layoutKey" />
    <AppMessage v-if="isIFrame" />
    <DxLoaderBlock :blocked="isLoadingSpinner" />
    <DxLoaderBar v-if="!isIFrame" />
    <DxLoaderSpinner />
    <DxToast />
    <ConfirmDialog>
      <template #message="slotProps">
        <Icon icon="line-md:confirm" :class="slotProps.message.icon" />
        <span class="p-confirm-dialog-message" v-html="slotProps.message.message"></span>
      </template>
    </ConfirmDialog>
    <DynamicDialog />
    <WidgetContainerModal />
    <TwoFactorRequiredModal />
  </template>
</template>

<style scoped lang="scss" />
